import { api } from './api';

// Types
export interface SupportCategory {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  priority: number;
  is_active: number;
}

export interface SupportConversation {
  id: string;
  mda_id: string;
  category_id: string;
  subject: string;
  status: 'open' | 'assigned' | 'pending_user' | 'resolved' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  initiated_by: string;
  initiated_by_name: string;
  initiated_by_email: string;
  assigned_to: string | null;
  assigned_to_name: string | null;
  assigned_at: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_notes: string | null;
  rating: number | null;
  rating_feedback: string | null;
  last_message_at: string | null;
  last_message_by: string | null;
  created_at: string;
  updated_at: string;
  // Joined fields
  category_name?: string;
  category_icon?: string;
  mda_name?: string;
  mda_code?: string;
  unread_count?: number;
}

export interface SupportMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  sender_name: string;
  sender_type: 'user' | 'admin' | 'system';
  content: string;
  message_type: 'text' | 'system' | 'attachment' | 'status_change' | 'ai_response';
  attachment_url: string | null;
  attachment_name: string | null;
  attachment_type: string | null;
  is_read: number;
  read_at: string | null;
  created_at: string;
}

export interface AIResponse {
  response: string;
  confidence: number;
  canResolve: boolean;
  relatedTopics?: string[];
  suggestHumanSupport?: boolean;
  escalationReason?: string;
}

export interface CannedResponse {
  id: string;
  title: string;
  content: string;
  category_id: string | null;
  shortcut: string | null;
  usage_count: number;
}

export interface ConversationStats {
  total: number;
  open: number;
  assigned: number;
  pending_user: number;
  resolved: number;
  closed: number;
  avgResponseTime: number;
  avgRating: number;
}

export interface AgentStats {
  assigned: number;
  resolved: number;
  avgRating: number;
  activeConversations: number;
}

// ============================================
// Institution User API (prefixed with /api/v1/support)
// ============================================

/**
 * Get support categories
 */
export async function getCategories() {
  return api.get<SupportCategory[]>('/api/v1/support/categories');
}

/**
 * Get current user's support conversations
 */
export async function getMyConversations(params?: {
  page?: number;
  perPage?: number;
  status?: string;
}) {
  return api.get<SupportConversation[]>('/api/v1/support/conversations', params);
}

/**
 * Create a new support conversation
 */
export async function createConversation(data: {
  categoryId: string;
  subject: string;
  message: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
}) {
  return api.post<{
    conversation: SupportConversation;
    aiResponse: SupportMessage | null;
    aiCanHelp: boolean;
  }>('/api/v1/support/conversations', data);
}

/**
 * Get a specific conversation with messages
 */
export async function getConversation(id: string) {
  return api.get<{
    conversation: SupportConversation;
    messages: SupportMessage[];
  }>(`/api/v1/support/conversations/${id}`);
}

/**
 * Send a message in a conversation
 */
export async function sendMessage(conversationId: string, data: {
  content: string;
  attachmentUrl?: string;
  attachmentName?: string;
  attachmentType?: string;
}) {
  return api.post<{
    message: SupportMessage;
    aiResponse?: {
      message: SupportMessage;
      confidence: number;
      relatedTopics?: string[];
    };
  }>(`/api/v1/support/conversations/${conversationId}/messages`, data);
}

/**
 * Close a conversation
 */
export async function closeConversation(id: string) {
  return api.post<void>(`/api/v1/support/conversations/${id}/close`);
}

/**
 * Rate a resolved conversation
 */
export async function rateConversation(id: string, rating: number, feedback?: string) {
  return api.post<void>(`/api/v1/support/conversations/${id}/rate`, { rating, feedback });
}

/**
 * Get unread message count
 */
export async function getUnreadCount() {
  return api.get<{ unreadCount: number }>('/api/v1/support/unread-count');
}

/**
 * Ask AI assistant directly (without creating conversation)
 */
export async function askAI(question: string, context?: {
  messages?: Array<{ role: 'user' | 'assistant'; content: string }>;
  category?: string;
}) {
  return api.post<AIResponse>('/api/v1/support/ai/ask', { question, context });
}

/**
 * Submit AI feedback
 */
export async function submitAIFeedback(interactionId: string, wasHelpful: boolean) {
  return api.post<void>('/api/v1/support/ai/feedback', { interactionId, wasHelpful });
}

// ============================================
// Platform Admin API (prefixed with /api/platform/support)
// ============================================

/**
 * Get all conversations (platform admin)
 */
export async function getAllConversations(params?: {
  page?: number;
  perPage?: number;
  status?: string;
  priority?: string;
  categoryId?: string;
  mdaId?: string;
  assignedTo?: string;
  search?: string;
}) {
  return api.get<SupportConversation[]>('/api/platform/support/conversations', params);
}

/**
 * Get unassigned conversations queue
 */
export async function getConversationQueue() {
  return api.get<SupportConversation[]>('/api/platform/support/conversations/queue');
}

/**
 * Get conversation details (platform admin)
 */
export async function getAdminConversation(id: string) {
  return api.get<{
    conversation: SupportConversation;
    messages: SupportMessage[];
  }>(`/api/platform/support/conversations/${id}`);
}

/**
 * Assign conversation to self or another admin
 */
export async function assignConversation(id: string, assignTo?: string, assignToName?: string) {
  return api.post<void>(`/api/platform/support/conversations/${id}/assign`, {
    assignTo,
    assignToName,
  });
}

/**
 * Send admin reply
 */
export async function sendAdminMessage(conversationId: string, data: {
  content: string;
  attachmentUrl?: string;
  attachmentName?: string;
  attachmentType?: string;
}) {
  return api.post<SupportMessage>(`/api/platform/support/conversations/${conversationId}/messages`, data);
}

/**
 * Resolve a conversation
 */
export async function resolveConversation(id: string, notes?: string) {
  return api.post<void>(`/api/platform/support/conversations/${id}/resolve`, { notes });
}

/**
 * Update conversation priority
 */
export async function updatePriority(id: string, priority: 'low' | 'normal' | 'high' | 'urgent') {
  return api.put<void>(`/api/platform/support/conversations/${id}/priority`, { priority });
}

/**
 * Get support dashboard statistics
 */
export async function getStats() {
  return api.get<ConversationStats>('/api/platform/support/stats');
}

/**
 * Get current agent statistics
 */
export async function getAgentStats() {
  return api.get<AgentStats>('/api/platform/support/agents/stats');
}

/**
 * Update agent availability status
 */
export async function updateAgentStatus(status: 'online' | 'away' | 'busy' | 'offline') {
  return api.put<void>('/api/platform/support/agents/status', { status });
}

/**
 * Get admin unread count
 */
export async function getAdminUnreadCount() {
  return api.get<{ unreadCount: number }>('/api/platform/support/unread-count');
}

/**
 * Get canned responses
 */
export async function getCannedResponses(categoryId?: string) {
  return api.get<CannedResponse[]>('/api/platform/support/canned-responses', { categoryId });
}

/**
 * Create canned response
 */
export async function createCannedResponse(data: {
  title: string;
  content: string;
  categoryId?: string;
  shortcut?: string;
}) {
  return api.post<void>('/api/platform/support/canned-responses', data);
}

/**
 * Delete canned response
 */
export async function deleteCannedResponse(id: string) {
  return api.delete<void>(`/api/platform/support/canned-responses/${id}`);
}

/**
 * Track canned response usage
 */
export async function trackCannedResponseUsage(id: string) {
  return api.post<void>(`/api/platform/support/canned-responses/${id}/use`);
}

/**
 * Get platform categories
 */
export async function getPlatformCategories() {
  return api.get<SupportCategory[]>('/api/platform/support/categories');
}
